import React, { useEffect, useState, useCallback } from 'react';
import { Category } from 'types/Category';
import { Select } from 'components';
import type { Option } from 'react-select';
import { Show } from 'components/Functional';
import { useWindowSize, DeviceSize } from 'hooks/useWindowSize';
import { updatePaginationState } from 'store/ui/actions';
import { useDispatch, useSelector } from 'react-redux';
import { paginationStateSelector } from 'store/ui/selectors';
import { goToAnchor, removeHash } from 'react-scrollable-anchor';
import { makePaginationUrl } from 'helpers/pagination';
import { redirectToPage } from 'helpers/navigation';
import { PaginationPageLimit } from 'constants/clp';
import {
  CLPLoadMoreContainer,
  CLPPageLink,
  CLPPaginationNavLink,
  CLPPaginationWrapper,
  CLPProductNavWrapper
} from 'components/Pagination/CLPPagination/styled';
import { LoadMoreButton } from 'components/Pagination/styled';

interface CLPPaginationProps {
  totalPages: number;
  category: Category;
  basePath: string;
  paginationPageLimits: PaginationPageLimit[];
}

export const CLPPagination = ({ totalPages, category, paginationPageLimits, basePath }: CLPPaginationProps): JSX.Element => {
  const dispatch = useDispatch();
  const { deviceSize } = useWindowSize();
  const uiState = useSelector(paginationStateSelector);
  const [loadedMoreScrollY, setLoadedMoreScrollY] = useState(-1);

  const shouldShowPageSelector = totalPages > 1;

  const goToTargetPage = (targetPageIndex: number) =>
    dispatch(updatePaginationState(category.categoryId, targetPageIndex, uiState.displayCount));

  const makePaginationPageLink = (
    targetPageIndex: number,
    targetPageNumber: number,
    key?: string | number,
    targetPageText?: string | number,
    active = false,
    onClick?: () => void
  ) => (
    <CLPPageLink
      key={key || targetPageNumber}
      tabIndex={0}
      to={makePaginationUrl(category.urlPath, targetPageNumber)}
      $active={active}
      onClick={() => {
        onClick && onClick();
      }}
      onKeyPress={() => goToTargetPage(targetPageIndex)}
    >
      {targetPageText || targetPageNumber}
    </CLPPageLink>
  );

  const renderPageLinks = (_totalPages: number, _currPage: number) => {
    const components = [];
    for (let targetPageIndex = 0; targetPageIndex < _totalPages; targetPageIndex++) {
      const targetPageNumber = targetPageIndex + 1;
      const isActive = targetPageIndex === uiState.currentPage;
      const onClick = () => {
        goToAnchor('product-grid', false);
        removeHash();
      };
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, undefined, undefined, isActive, onClick));
    }

    return components;
  };

  const renderTruncatedPageLinks = (_totalPages: number, _currIndex: number) => {
    const components = [];
    const maxIndex = _totalPages - 1;
    const indexBeforeCurr = _currIndex - 1;
    const indexAfterCurr = _currIndex + 1;

    // Ellipses that render for truncation
    if (_currIndex > 1 && indexBeforeCurr !== 1) {
      const targetPageIndex = indexBeforeCurr - 2;
      const targetPageNumber = targetPageIndex + 1;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-prev-ellipses', '...'));
    }

    // Renders the (current page number - 2) as long as it is within bounds
    if (indexBeforeCurr - 1 > -1) {
      const targetPageIndex = indexBeforeCurr - 1;
      const targetPageNumber = targetPageIndex + 1;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-minus-2'));
    }

    // Renders the previous page number as long as it is within bounds
    if (indexBeforeCurr > -1) {
      const targetPageIndex = indexBeforeCurr;
      const targetPageNumber = targetPageIndex + 1;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-minus-1'));
    }

    // Current/active page
    const currentPageNumber = _currIndex + 1;
    components.push(makePaginationPageLink(_currIndex, currentPageNumber, 'page-current', undefined, true));

    // Renders the next page number as long as it is within bounds
    if (indexAfterCurr <= maxIndex) {
      const targetPageIndex = indexAfterCurr;
      const targetPageNumber = targetPageIndex + 1;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-plus-1'));
    }

    // Renders the (current page number + 2) as long as it is within bounds
    if (indexAfterCurr + 1 <= maxIndex) {
      const targetPageIndex = indexAfterCurr + 1;
      const targetPageNumber = targetPageIndex + 2;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-plus-2'));
    }

    // Ellipses that render for truncation
    if (indexAfterCurr + 2 <= maxIndex) {
      const targetPageIndex = indexAfterCurr + 2;
      const targetPageNumber = targetPageIndex + 1;
      components.push(makePaginationPageLink(targetPageIndex, targetPageNumber, 'page-next-ellipses', '...'));
    }

    return components;
  };

  const loadMore = useCallback(() => {
    setLoadedMoreScrollY(window.scrollY);
    dispatch(updatePaginationState(category.categoryId, uiState.currentPage, uiState.displayCount + uiState.displayCount));
  }, [dispatch, category, uiState]);

  const handleScrollEvent = useCallback(() => {
    if (loadedMoreScrollY !== -1) {
      window.scrollTo(0, loadedMoreScrollY);
      setLoadedMoreScrollY(-1);
    }
  }, [loadedMoreScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollEvent);
    return () => {
      window.removeEventListener('scroll', handleScrollEvent);
    };
  }, [handleScrollEvent]);

  const prevPageIndex = uiState.currentPage - 1;
  const prevPageNumber = prevPageIndex + 1;
  const prevDisabled = uiState.currentPage === 0;

  const nextPageIndex = uiState.currentPage + 1;
  const nextPageNumber = nextPageIndex + 1;
  const nextDisabled = uiState.currentPage === totalPages - 1;

  const onChangeProductCount = (option: Option) => {
    // If the new number is higher than the maxProducts, reset back to page 1
    const pageNumber = uiState.currentPage + 1;
    const maxProducts = totalPages * uiState.displayCount;
    const newMaxProducts = option.value * pageNumber;

    // Include existing params to keep filters, and set count
    const params = new URLSearchParams(window.location.search);
    params.set('count', option.value);

    if (newMaxProducts > maxProducts) {
      return redirectToPage(`${basePath}/?${params.toString()}`);
    }
    return redirectToPage(`?${params.toString()}`);
  };

  const DesktopPagination = (
    <CLPProductNavWrapper>
      <Select
        styles={{
          container: provided => ({
            ...provided,
            zIndex: 999
          })
        }}
        name="Page Limit"
        isLoading={false}
        value={paginationPageLimits.find(opt => opt.value === uiState.displayCount)}
        options={paginationPageLimits}
        onChange={onChangeProductCount}
        isSearchable={false}
        className="page-limit-select"
      />

      {shouldShowPageSelector && (
        <CLPPaginationWrapper>
          <CLPPaginationNavLink
            tabIndex={0}
            to={prevDisabled ? '' : makePaginationUrl(category.urlPath, prevPageNumber)}
            disabled={prevDisabled}
            onClick={() => goToTargetPage(prevPageIndex)}
            onKeyPress={() => goToTargetPage(prevPageIndex)}
            rel="prev"
          >
            PREV
          </CLPPaginationNavLink>

          <Show when={deviceSize !== DeviceSize.SMALL}>{renderPageLinks(totalPages, uiState.currentPage)}</Show>
          <Show when={deviceSize === DeviceSize.SMALL}>{renderTruncatedPageLinks(totalPages, uiState.currentPage)}</Show>

          <CLPPaginationNavLink
            tabIndex={0}
            to={nextDisabled ? '' : makePaginationUrl(category.urlPath, nextPageNumber)}
            disabled={nextDisabled}
            onClick={() => goToTargetPage(nextPageIndex)}
            onKeyPress={() => goToTargetPage(nextPageIndex)}
            rel="next"
          >
            NEXT
          </CLPPaginationNavLink>
        </CLPPaginationWrapper>
      )}
    </CLPProductNavWrapper>
  );

  const MobilePagination = shouldShowPageSelector && (
    <CLPLoadMoreContainer>
      <LoadMoreButton onClick={loadMore}>LOAD MORE</LoadMoreButton>
    </CLPLoadMoreContainer>
  );

  return deviceSize === DeviceSize.SMALL ? MobilePagination : DesktopPagination;
};
